import { useEffect, useState } from "react";
import { UsersRound, Plus, Trash2, Loader2, Mail, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PageHeader, PrimaryButton, Card } from "@/components/design-system";

function timeAgo(iso) {
    if (!iso) return "hiç";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return "şimdi";
    if (mins < 60) return `${mins} dk önce`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} sa önce`;
    return `${Math.floor(hours / 24)} gün önce`;
}

const initials = (name = "") => name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?";

export default function TeamPage({ embedded = false }) {
    const { session } = useAuth();
    const isOwner = session?.user?.role === "owner";
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviting, setInviting] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteName, setInviteName] = useState("");
    const [inviteBusy, setInviteBusy] = useState(false);
    const [removeTarget, setRemoveTarget] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/team");
            setTeam(data);
        } catch {
            toast.error("Ekip yüklenemedi");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const invite = async () => {
        if (!inviteEmail.trim() || !inviteName.trim()) return;
        setInviteBusy(true);
        try {
            await api.post("/team", { email: inviteEmail.trim(), name: inviteName.trim() });
            toast.success(`${inviteName} eklendi — artık ${inviteEmail} ile giriş yapabilir`);
            setInviteEmail(""); setInviteName("");
            setInviteOpen(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Davet edilemedi");
        } finally {
            setInviteBusy(false);
        }
    };

    const remove = async () => {
        if (!removeTarget) return;
        setInviting(true);
        try {
            await api.delete(`/team/${removeTarget.id}`);
            toast.success("Üye kaldırıldı");
            setRemoveTarget(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaldırılamadı");
        } finally {
            setInviting(false);
        }
    };

    // Member list body — shared between embedded (wrapped in Section) and
    // standalone (own Card) renderings.
    const memberList = loading ? (
        <div className="p-6 text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Yükleniyor…
        </div>
    ) : (
        <div className="divide-y divide-border">
            {team.map((m) => (
                <div
                    key={m.id}
                    className="py-3.5 flex items-center gap-3 first:pt-0 last:pb-0"
                    data-testid={`team-row-${m.email}`}
                >
                    <Avatar className="h-9 w-9 border border-border">
                        <AvatarFallback className="text-[11px] font-semibold">{initials(m.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium truncate">{m.name}</span>
                            {m.id === session?.user?.id && <Badge variant="outline" className="text-[9px] uppercase">Siz</Badge>}
                        </div>
                        <div className="text-[11px] text-muted-foreground font-mono truncate">{m.email}</div>
                    </div>
                    <div className="hidden sm:block text-[11px] text-muted-foreground text-right">
                        <div>Son giriş</div>
                        <div className="font-mono">{timeAgo(m.last_login_at)}</div>
                    </div>
                    <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${m.role === "owner" ? "border-foreground/30" : ""}`}
                    >
                        {m.role === "owner" ? "Sahip" : "Üye"}
                    </Badge>
                    {isOwner && m.role !== "owner" && m.id !== session?.user?.id && (
                        <Button
                            variant="ghost" size="icon"
                            onClick={() => setRemoveTarget(m)}
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            data-testid={`team-remove-${m.email}`}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div className="space-y-6" data-testid="team-page">
            {embedded ? (
                // Embedded inside Settings → match Profile's Section chrome:
                // rounded-xl border + header (title/desc + action) + body.
                <div className="rounded-xl border border-border bg-surface">
                    <div className="px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3">
                        <div>
                            <h3 className="font-display text-base font-semibold tracking-tight">Ekip</h3>
                            <p className="text-xs text-muted-foreground mt-0.5 max-w-lg leading-relaxed">
                                Sayz davete özeldir. Sadece eklediğiniz e-postalar panele giriş yapabilir. Yeni üye eklediğinizde e-postasıyla OTP alarak giriş yapabilir.
                            </p>
                        </div>
                        {isOwner && (
                            <PrimaryButton onClick={() => setInviteOpen(true)} data-testid="team-invite-btn">
                                <Plus className="h-3.5 w-3.5" /> Üye Ekle
                            </PrimaryButton>
                        )}
                    </div>
                    <div className="p-5">{memberList}</div>
                </div>
            ) : (
                <>
                    <PageHeader
                        icon={UsersRound}
                        title="Ekip"
                        description="Sayz davete özeldir. Sadece eklediğiniz e-postalar panele giriş yapabilir. Yeni üye eklediğinizde e-postasıyla OTP alarak giriş yapabilir."
                        actions={
                            isOwner && (
                                <PrimaryButton onClick={() => setInviteOpen(true)} data-testid="team-invite-btn">
                                    <Plus className="h-3.5 w-3.5" /> Üye Ekle
                                </PrimaryButton>
                            )
                        }
                    />
                    <Card className="overflow-hidden">
                        <div className="px-5 py-4">{memberList}</div>
                    </Card>
                </>
            )}

            {/* Invite dialog */}
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                <DialogContent data-testid="team-invite-dialog">
                    <DialogHeader>
                        <DialogTitle>Yeni Üye Ekle</DialogTitle>
                        <DialogDescription>
                            Ekip üyeniz e-postasıyla panele giriş yapabilir. OTP kodu o e-postaya gönderilir.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <UserIcon className="h-3 w-3" /> Ad
                            </label>
                            <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)} placeholder="Ali Veli" className="h-9 text-sm" data-testid="invite-name-input" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                                <Mail className="h-3 w-3" /> E-posta
                            </label>
                            <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="ali@sirket.com" className="h-9 text-sm" data-testid="invite-email-input" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setInviteOpen(false)} disabled={inviteBusy}>İptal</Button>
                        <Button onClick={invite} disabled={inviteBusy || !inviteEmail || !inviteName} className="bg-brand hover:bg-brand/90 text-brand-fg" data-testid="invite-submit-btn">
                            {inviteBusy ? "Ekleniyor…" : "Ekle"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove confirmation */}
            <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
                <DialogContent data-testid="team-remove-dialog">
                    <DialogHeader>
                        <DialogTitle>Üyeyi Kaldır?</DialogTitle>
                        <DialogDescription>
                            <strong className="text-foreground">{removeTarget?.name}</strong> ({removeTarget?.email}) artık panele giriş yapamayacak. Bu işlem geri alınamaz.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setRemoveTarget(null)} disabled={inviting}>İptal</Button>
                        <Button variant="destructive" onClick={remove} disabled={inviting} data-testid="team-remove-confirm-btn">
                            {inviting ? "Kaldırılıyor…" : "Kaldır"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
